import React, { useState } from 'react';
import { Copy, Download, Calendar, Tag } from 'lucide-react';
import { useData } from '../context/DataContext';
import { highlightSQL } from '../utils/sqlHighlight';

const MultiSectionPage = ({ script }) => {
  const { trackDownload } = useData();
  const [copiedStates, setCopiedStates] = useState({});

  const handleCopy = async (content, scriptIndex, sectionIndex) => {
    try {
      await navigator.clipboard.writeText(content);
      const key = `${sectionIndex}-${scriptIndex}`;
      setCopiedStates(prev => ({ ...prev, [key]: true }));
      setTimeout(() => {
        setCopiedStates(prev => ({ ...prev, [key]: false }));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleDownload = (content, title, scriptId) => {
    const element = document.createElement('a');
    const file = new Blob([content], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.sql`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    trackDownload(scriptId);
  };

  return (
    <div className="page-container">
      <div className="script-header">
        <h1 className="script-title">{script.title}</h1>
        <div className="script-meta">
          <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <Tag size={16} />
            {script.category} - {script.subcategory}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <Calendar size={16} />
            {new Date(script.createdAt).toLocaleDateString()}
          </span>
        </div>
      </div>

      <div className="script-content">
        <div className="script-description">
          {script.description}
        </div>

        {script.sections.map((section, sectionIndex) => (
          <div key={section.id} style={{ marginBottom: '40px' }}>
            <div style={{ 
              background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)', 
              color: 'white', 
              padding: '15px', 
              borderRadius: '8px', 
              marginBottom: '20px' 
            }}>
              <h2 style={{ margin: 0, marginBottom: '8px', fontSize: '1.5rem' }}>{section.title}</h2>
              <p style={{ margin: 0, opacity: 0.9 }}>{section.description}</p>
            </div>

            {section.scripts.map((subScript, scriptIndex) => {
              const copyKey = `${sectionIndex}-${scriptIndex}`;
              const isCopied = copiedStates[copyKey];
              
              return (
                <div key={scriptIndex} style={{ marginBottom: '30px' }}>
                  <h3 style={{ 
                    color: '#2c3e50', 
                    marginBottom: '10px', 
                    fontSize: '1.2rem',
                    fontWeight: '600'
                  }}>
                    {subScript.title}
                  </h3>
                  
                  <p style={{ 
                    color: '#6c757d', 
                    marginBottom: '15px', 
                    lineHeight: '1.6' 
                  }}>
                    {subScript.description}
                  </p>

                  <div className="code-block">
                    <div className="code-header">
                      <span className="code-title">SQL Script</span>
                      <div className="code-actions">
                        <button 
                          onClick={() => handleCopy(subScript.content, scriptIndex, sectionIndex)} 
                          className={`btn ${isCopied ? 'btn-success' : 'btn-primary'}`}
                        >
                          <Copy size={16} />
                          {isCopied ? 'Copied!' : 'Copy'}
                        </button>
                        <button 
                          onClick={() => handleDownload(subScript.content, subScript.title, script.id)} 
                          className="btn btn-secondary"
                        >
                          <Download size={16} />
                          Download
                        </button>
                      </div>
                    </div>
                    <div className="code-content" dangerouslySetInnerHTML={{ __html: highlightSQL(subScript.content) }}>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}

        <div style={{ 
          marginTop: '30px', 
          padding: '20px', 
          background: '#f8f9fa', 
          borderRadius: '8px', 
          border: '1px solid #e9ecef' 
        }}>
          <h3 style={{ marginBottom: '15px', color: '#495057' }}>Usage Notes</h3>
          <ul style={{ color: '#6c757d', lineHeight: '1.6' }}>
            <li>Always test scripts in a development environment before running in production</li>
            <li>Review and understand the script logic before execution</li>
            <li>Ensure you have appropriate permissions to run the script</li>
            <li>Consider the impact on system performance during execution</li>
            <li>Take necessary backups before making any changes</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default MultiSectionPage;